import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Warning } from "@phosphor-icons/react";

export default function StudentTakeTest() {
  const { id } = useParams();
  const nav = useNavigate();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const startRef = useRef(Date.now());
  const answersRef = useRef({});
  const submitOnceRef = useRef(false);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    api.get(`/tests/${id}`).then(r => {
      setTest(r.data);
      startRef.current = Date.now();
    }).catch((e) => {
      const msg = e?.response?.data?.detail || "Unable to load test";
      toast.error(typeof msg === "string" ? msg : "Unable to load test");
      if (e?.response?.status === 403) nav("/student/tests");
    });
  }, [id, nav]);

  useEffect(() => {
    if (!test || result) return;
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [test, result]);

  const total = test ? test.duration_minutes * 60 : 0;
  const remaining = Math.max(0, total - elapsed);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  const submit = useCallback(async (options = {}) => {
    const { fromFocusLoss = false } = typeof options === "object" ? options : {};
    if (submitOnceRef.current) return;
    submitOnceRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await api.post("/attempts", { test_id: id, answers: answersRef.current });
      setResult(data);
      if (fromFocusLoss) {
        toast.warning("Test submitted: you left this page or switched to another tab or window.");
      } else {
        toast.success("Submitted");
      }
    } catch (e) {
      submitOnceRef.current = false;
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }, [id]);

  useEffect(() => {
    if (test && !result && remaining === 0 && elapsed > 0) submit();
  }, [remaining, test, result, elapsed, submit]);

  useEffect(() => {
    submitOnceRef.current = false;
  }, [id]);

  /** Switching tabs, minimizing the window, or many full-screen app switches set visibility to hidden. */
  useEffect(() => {
    if (!test || result) return;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        submit({ fromFocusLoss: true });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [test, result, submit]);

  if (!test) return <div className="font-serif text-forest">Loading test…</div>;

  if (result) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="overline mb-2">Result</div>
        <h1 className="font-serif text-5xl text-forest">{test.title}</h1>
        <div className="mt-8 card-academic p-8 text-center">
          <div className="overline">Your score</div>
          <div className="font-mono text-6xl text-forest font-semibold my-4">{result.score} <span className="text-mossy">/ {result.max_score}</span></div>
          <div className="font-mono text-2xl text-terracotta">{result.percentage}%</div>
          <div className="mt-6 flex justify-center gap-3">
            <button onClick={() => nav("/student/results")} className="btn-primary" data-testid="goto-results">View analysis</button>
            <button onClick={() => nav("/student")} className="btn-secondary" data-testid="back-dash">Back to dashboard</button>
          </div>
        </div>
      </div>
    );
  }

  const blockClipboard = (e) => {
    e.preventDefault();
  };

  return (
    <div>
      <div className="sticky top-20 z-20 bg-bone/90 backdrop-blur-sm -mx-8 px-8 py-3 border-b border-stone flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="overline">Test in progress</div>
            <div className="font-serif text-2xl text-forest">{test.title}</div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-sm font-mono">
              {Object.keys(answers).length} / {test.questions.length} answered
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-forest text-ivory font-mono text-lg">
              <Clock size={18} /> {mm}:{ss}
            </div>
          </div>
        </div>
        <div
          className="flex gap-3 p-4 rounded-lg border border-terracotta/40 bg-sand/60 text-sm text-ink leading-snug max-w-3xl"
          role="status"
          aria-live="polite"
          data-testid="proctor-notice"
        >
          <Warning className="shrink-0 text-terracotta" size={22} weight="fill" aria-hidden />
          <div>
            <strong className="text-forest">Proctored exam.</strong>{" "}
            Your answers are submitted automatically if you leave this browser tab or window—for example switching to another
            tab, another app, or minimizing the browser. Stay on this screen until you finish or click Submit test. Copying or
            cutting question text from this page is disabled.
          </div>
        </div>
      </div>

      <div
        className="mt-6 space-y-5 max-w-3xl select-none"
        onCopy={blockClipboard}
        onCut={blockClipboard}
        data-testid="test-body"
      >
        {test.questions.map((q, i) => (
          <div key={q.id} className="card-academic p-6">
            <div className="overline mb-2">Question {i + 1} · {q.marks} {q.marks === 1 ? "mark" : "marks"}</div>
            <div className="text-ink text-lg mb-4">{q.text}</div>
            <div className="space-y-2">
              {q.options?.map((o, oi) => (
                <label key={oi} className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${Number(answers[q.id]) === oi ? "border-forest bg-sand/50" : "border-stone hover:bg-sand/30"}`}>
                  <input type="radio" name={q.id} checked={Number(answers[q.id]) === oi}
                    onChange={() => setAnswers({ ...answers, [q.id]: oi })}
                    data-testid={`ans-${q.id}-${oi}`} className="mt-1" />
                  <span><span className="font-mono mr-2">{String.fromCharCode(65 + oi)}.</span>{o}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 bg-bone/90 backdrop-blur-sm border-t border-stone -mx-8 px-8 py-4 flex justify-end gap-3 mt-6">
        <button onClick={() => nav("/student/tests")} className="btn-ghost" data-testid="cancel-attempt">Cancel</button>
        <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="submit-attempt">
          {submitting ? "Submitting…" : "Submit test"}
        </button>
      </div>
    </div>
  );
}
